import React, { useEffect, useState } from "react";
import { api, fmtINR, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Play, Users as UsersIcon } from "@phosphor-icons/react";

export default function Payroll() {
  const [tab, setTab] = useState("employees");
  const [emps, setEmps] = useState([]);
  const [runs, setRuns] = useState([]);
  const [slips, setSlips] = useState([]);
  const [showEmp, setShowEmp] = useState(false);
  const [empForm, setEmpForm] = useState({
    name: "", email: "", phone: "", designation: "", date_of_joining: new Date().toISOString().slice(0,10),
    pan: "", basic: 30000, hra: 15000, special_allowance: 10000, other_allowance: 0,
    pf_applicable: true, esi_applicable: true, pt_applicable: true, tds_rate: 0,
  });
  const [runMonth, setRunMonth] = useState(new Date().toISOString().slice(0,7));

  const load = async () => {
    const [e, r] = await Promise.all([api.get("/payroll/employees"), api.get("/payroll/runs")]);
    setEmps(e.data); setRuns(r.data);
  };
  useEffect(() => { load(); }, []);

  const loadSlips = async (month) => {
    const { data } = await api.get(`/payroll/slips/${month}`);
    setSlips(data);
  };

  const submitEmp = async (e) => {
    e.preventDefault();
    try {
      await api.post("/payroll/employees", { ...empForm,
        basic: Number(empForm.basic), hra: Number(empForm.hra),
        special_allowance: Number(empForm.special_allowance),
        other_allowance: Number(empForm.other_allowance), tds_rate: Number(empForm.tds_rate),
      });
      toast.success("Employee added");
      setShowEmp(false);
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const runPayroll = async () => {
    if (!confirm(`Run payroll for ${runMonth}? This posts salary vouchers.`)) return;
    try {
      const { data } = await api.post("/payroll/run", { month: runMonth });
      toast.success(`Payroll run: ${data.pay_run.employee_count} employees · Net ₹${data.pay_run.net}`);
      load();
      loadSlips(runMonth);
      setTab("slips");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5 lb-fade" data-testid="payroll-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="lb-section-head">HR & Payroll</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Payroll</h1>
          <p className="text-neutral-600 text-sm mt-1">Indian statutory compliance: PF (12% cap ₹15k), ESI (0.75%/3.25%), PT ₹200, TDS</p>
        </div>
        <div className="flex gap-2">
          <input type="month" className="lb-input w-40" value={runMonth}
                 onChange={(e) => setRunMonth(e.target.value)} data-testid="pay-month" />
          <button className="lb-btn lb-btn-accent" onClick={runPayroll} data-testid="run-payroll-btn">
            <Play size={14} /> Run Payroll
          </button>
          <button className="lb-btn" onClick={() => setShowEmp((s) => !s)} data-testid="add-emp-btn">
            <Plus size={14} /> Employee
          </button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <div className="lb-kpi lb-kpi-teal"><div className="lb-kpi-label">Employees</div><div className="lb-kpi-value">{emps.length}</div></div>
        <div className="lb-kpi lb-kpi-revenue"><div className="lb-kpi-label">Monthly Payroll (Gross)</div><div className="lb-kpi-value mono">{fmtINR(emps.reduce((s, e) => s + e.basic + e.hra + e.special_allowance + e.other_allowance, 0))}</div></div>
        <div className="lb-kpi lb-kpi-gst"><div className="lb-kpi-label">Pay Runs</div><div className="lb-kpi-value">{runs.length}</div></div>
        <div className="lb-kpi lb-kpi-cash"><div className="lb-kpi-label">Latest Net Paid</div><div className="lb-kpi-value mono">{runs[0] ? fmtINR(runs[0].net) : "—"}</div></div>
      </div>

      {showEmp && (
        <form onSubmit={submitEmp} className="lb-card p-6 grid grid-cols-4 gap-3">
          <input required className="lb-input col-span-2" placeholder="Full name" value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} data-testid="emp-name" />
          <input type="email" required className="lb-input" placeholder="Email" value={empForm.email} onChange={(e) => setEmpForm({ ...empForm, email: e.target.value })} data-testid="emp-email" />
          <input className="lb-input" placeholder="Phone" value={empForm.phone} onChange={(e) => setEmpForm({ ...empForm, phone: e.target.value })} />
          <input required className="lb-input col-span-2" placeholder="Designation" value={empForm.designation} onChange={(e) => setEmpForm({ ...empForm, designation: e.target.value })} />
          <input type="date" required className="lb-input" value={empForm.date_of_joining} onChange={(e) => setEmpForm({ ...empForm, date_of_joining: e.target.value })} />
          <input className="lb-input font-mono uppercase" maxLength={10} placeholder="PAN" value={empForm.pan} onChange={(e) => setEmpForm({ ...empForm, pan: e.target.value.toUpperCase() })} />
          <div><label className="lb-label">Basic</label><input type="number" className="lb-input font-mono text-right" value={empForm.basic} onChange={(e) => setEmpForm({ ...empForm, basic: e.target.value })} data-testid="emp-basic" /></div>
          <div><label className="lb-label">HRA</label><input type="number" className="lb-input font-mono text-right" value={empForm.hra} onChange={(e) => setEmpForm({ ...empForm, hra: e.target.value })} /></div>
          <div><label className="lb-label">Special</label><input type="number" className="lb-input font-mono text-right" value={empForm.special_allowance} onChange={(e) => setEmpForm({ ...empForm, special_allowance: e.target.value })} /></div>
          <div><label className="lb-label">Other</label><input type="number" className="lb-input font-mono text-right" value={empForm.other_allowance} onChange={(e) => setEmpForm({ ...empForm, other_allowance: e.target.value })} /></div>
          <div className="col-span-4 flex gap-4 items-center">
            <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={empForm.pf_applicable} onChange={(e) => setEmpForm({ ...empForm, pf_applicable: e.target.checked })} /> PF</label>
            <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={empForm.esi_applicable} onChange={(e) => setEmpForm({ ...empForm, esi_applicable: e.target.checked })} /> ESI</label>
            <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={empForm.pt_applicable} onChange={(e) => setEmpForm({ ...empForm, pt_applicable: e.target.checked })} /> PT</label>
            <div className="flex items-center gap-1 text-xs">TDS %:<input type="number" step="0.01" className="lb-input w-20 font-mono text-right" value={empForm.tds_rate} onChange={(e) => setEmpForm({ ...empForm, tds_rate: e.target.value })} /></div>
          </div>
          <div className="col-span-4 flex gap-2"><button className="lb-btn" data-testid="emp-submit">Save</button><button type="button" className="lb-btn lb-btn-ghost" onClick={() => setShowEmp(false)}>Cancel</button></div>
        </form>
      )}

      <div className="lb-card">
        <div className="flex border-b border-neutral-200">
          {[
            { k: "employees", l: `Employees (${emps.length})` },
            { k: "runs", l: `Pay Runs (${runs.length})` },
            { k: "slips", l: "Salary Slips" },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
                    className={"px-4 py-3 text-sm font-semibold border-b-2 " +
                      (tab === t.k ? "border-[#0A66C2] text-[#0A66C2]" : "border-transparent text-neutral-500")}
                    data-testid={`pay-tab-${t.k}`}>{t.l}</button>
          ))}
        </div>

        {tab === "employees" && (
          <table className="lb-table">
            <thead><tr><th>Name</th><th>Designation</th><th>PAN</th><th className="text-right">Basic</th><th className="text-right">HRA</th><th className="text-right">Gross</th><th>Statutory</th></tr></thead>
            <tbody>
              {emps.map((e) => (
                <tr key={e.id}>
                  <td className="font-semibold">{e.name}<div className="text-xs text-neutral-500">{e.email}</div></td>
                  <td className="text-sm">{e.designation}</td>
                  <td className="font-mono text-xs">{e.pan || "—"}</td>
                  <td className="amt">{fmtINR(e.basic)}</td>
                  <td className="amt">{fmtINR(e.hra)}</td>
                  <td className="amt font-semibold">{fmtINR(e.basic + e.hra + e.special_allowance + e.other_allowance)}</td>
                  <td className="text-xs">
                    {e.pf_applicable && <span className="lb-badge lb-badge-info mr-1">PF</span>}
                    {e.esi_applicable && <span className="lb-badge lb-badge-teal mr-1">ESI</span>}
                    {e.pt_applicable && <span className="lb-badge mr-1">PT</span>}
                  </td>
                </tr>
              ))}
              {!emps.length && <tr><td colSpan={7} className="text-center py-10 text-neutral-500">No employees. Click Employee to add.</td></tr>}
            </tbody>
          </table>
        )}

        {tab === "runs" && (
          <table className="lb-table">
            <thead><tr><th>Month</th><th className="text-right">Employees</th><th className="text-right">Gross</th><th className="text-right">PF</th><th className="text-right">ESI</th><th className="text-right">PT</th><th className="text-right">TDS</th><th className="text-right">Net Paid</th><th></th></tr></thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td className="font-mono">{r.month}</td>
                  <td className="num">{r.employee_count}</td>
                  <td className="amt">{fmtINR(r.gross)}</td>
                  <td className="amt">{fmtINR(r.pf_total)}</td>
                  <td className="amt">{fmtINR(r.esi_total)}</td>
                  <td className="amt">{fmtINR(r.pt_total)}</td>
                  <td className="amt">{fmtINR(r.tds_total)}</td>
                  <td className="amt font-semibold text-emerald-600">{fmtINR(r.net)}</td>
                  <td><button className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => { loadSlips(r.month); setTab("slips"); }}>Slips →</button></td>
                </tr>
              ))}
              {!runs.length && <tr><td colSpan={9} className="text-center py-10 text-neutral-500">No pay runs. Click Run Payroll.</td></tr>}
            </tbody>
          </table>
        )}

        {tab === "slips" && (
          <div>
            <div className="p-3 border-b border-neutral-200 flex items-center gap-2">
              <input type="month" className="lb-input w-40" value={runMonth}
                     onChange={(e) => { setRunMonth(e.target.value); loadSlips(e.target.value); }} />
              <button className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => loadSlips(runMonth)}>Load</button>
            </div>
            <table className="lb-table">
              <thead><tr><th>Employee</th><th>Designation</th><th className="text-right">Gross</th><th className="text-right">PF</th><th className="text-right">ESI</th><th className="text-right">PT</th><th className="text-right">TDS</th><th className="text-right">Net Pay</th></tr></thead>
              <tbody>
                {slips.map((s) => (
                  <tr key={s.id}>
                    <td className="font-semibold">{s.employee_name}</td>
                    <td className="text-sm">{s.designation}</td>
                    <td className="amt">{fmtINR(s.gross)}</td>
                    <td className="amt">{fmtINR(s.pf_employee)}</td>
                    <td className="amt">{fmtINR(s.esi_employee)}</td>
                    <td className="amt">{fmtINR(s.pt)}</td>
                    <td className="amt">{fmtINR(s.tds)}</td>
                    <td className="amt font-semibold text-emerald-600">{fmtINR(s.net_pay)}</td>
                  </tr>
                ))}
                {!slips.length && <tr><td colSpan={8} className="text-center py-10 text-neutral-500">Select a month to view slips</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
